import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import Icon from '../components/Icons';
import toast from 'react-hot-toast';
import { Plus, Search, Edit2, Trash2, Eye, X, FolderKanban, FileText, Upload, Wrench, Settings, PackageMinus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { totalesPorPersona, fmtHoras } from '../utils/horasObra';

const FORMAS_PAGO = [
  '50% anticipo, 50% al finalizar','30% anticipo, 70% al finalizar',
  '100% anticipado','100% contra entrega',
  '50% anticipo, 25% avance, 25% al finalizar',
  'Crédito 30 días','Crédito 60 días','A convenir',
];
const ESTADOS = [
  { value:'pendiente', label:'Pendiente', badge:'badge-yellow' },
  { value:'activo',    label:'Activo',    badge:'badge-blue' },
  { value:'finalizado',label:'Finalizado',badge:'badge-green' },
  { value:'pausa',     label:'En pausa',  badge:'badge-orange' },
  { value:'debaja',    label:'De baja',   badge:'badge-red' },
];
const emptyForm = {
  clientId:'', citaId:'', nombre:'', descripcion:'', atencionNombre:'',
  tipoVenta:'servicio', categoriaVenta:'',
  presupuesto:'', presupuestoFinal:'',
  costo:'', gastosAdministrativos:'', costoTotal:'', utilidad:'', gananciaBrutaProtegida:'20',
  formaPago:'', utilidadReal:'', valorVariacion:'',
  estado:'pendiente', aprobado:false, relevancia:false,
  fechaEnvioOferta:'', fechaInicioObra:'', fechaFinEstimada:'', fechaFinReal:'',
  seguimiento:'', documentos:[],
  facturaNumero:'', facturaPdfNombre:'', facturaPdfData:'',
};
const badgeEstado = (e) => { const f=ESTADOS.find(s=>s.value===e); return <span className={`badge ${f?.badge||'badge-gray'}`}>{f?.label||e}</span>; };
const fmt = (n) => parseFloat(n||0).toLocaleString('es-EC',{minimumFractionDigits:2,maximumFractionDigits:2});
const verPdfEnNuevaPestana = (dataUri) => {
  try {
    const [meta, b64] = dataUri.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const bstr = atob(b64);
    const u8arr = new Uint8Array(bstr.length);
    for (let i=0;i<bstr.length;i++) u8arr[i] = bstr.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([u8arr], { type: mime }));
    window.open(url, '_blank');
    setTimeout(()=>URL.revokeObjectURL(url), 60000);
  } catch { window.open(dataUri, '_blank'); }
};
const Section = ({title}) => <h4 style={{margin:'18px 0 12px',color:'#7B0D0D',fontSize:13,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5,borderBottom:'2px solid #fee2e2',paddingBottom:6}}>{title}</h4>;

// ── Modal gestión de categorías (solo admin) ──────────────────────────────────
function ModalCategorias({ onClose, onSaved }) {
  const [categorias, setCategorias] = useState([]);
  const [tab, setTab] = useState('servicio');
  const [newLabel, setNewLabel] = useState('');
  const [newCodigo, setNewCodigo] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get('/project-categories');
    setCategorias(r.data);
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newLabel.trim()) { toast.error('Escribe un nombre'); return; }
    setSaving(true);
    try {
      await api.post('/project-categories', { tipo: tab, label: newLabel.trim(), codigo: newCodigo.trim() || undefined });
      setNewLabel(''); setNewCodigo('');
      await load();
      onSaved();
      toast.success('Categoría agregada');
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleCodigoChange = async (cat, codigo) => {
    setCategorias(prev => prev.map(c => c.id === cat.id ? { ...c, codigo } : c));
    if (codigo.length < 2) return; // esperar a que termine de escribir las 2 letras
    try {
      await api.put('/project-categories/' + cat.id, { label: cat.label, codigo });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar el código');
      await load(); // el código quedo rechazado (ej. duplicado) — vuelve a traer el valor real guardado
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try {
      await api.delete('/project-categories/' + id);
      await load();
      onSaved();
      toast.success('Eliminada');
    } catch(e) { toast.error('Error al eliminar'); }
  };

  const filtradas = categorias.filter(c => c.tipo === tab);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header">
          <h3 className="modal-title">Gestionar categorías</h3>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">
          <div style={{display:'flex',gap:8,marginBottom:16}}>
            {['servicio','bien'].map(t=>(
              <button key={t} type="button" onClick={()=>setTab(t)}
                style={{padding:'8px 20px',borderRadius:8,border:'2px solid '+(tab===t?'#7B0D0D':'#e5e5e5'),background:tab===t?'#7B0D0D':'white',color:tab===t?'white':'#555',cursor:'pointer',fontWeight:600,fontSize:13}}>
                {t==='servicio'?'Servicios':'Bienes'}
              </button>
            ))}
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:12,color:'#888',marginBottom:8}}>
              Categorías actuales de <strong>{tab==='servicio'?'Servicio':'Bien'}</strong>:
            </div>
            {filtradas.length === 0 ? (
              <div style={{fontSize:13,color:'#aaa',padding:'10px',textAlign:'center'}}>Sin categorías aún</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {filtradas.map(cat => (
                  <div key={cat.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#fafafa',borderRadius:8,border:'1px solid #e5e5e5'}}>
                    <span style={{flex:1,fontSize:13}}>{cat.label}</span>
                    <input value={cat.codigo||''} onChange={e=>handleCodigoChange(cat, e.target.value.toUpperCase().replace(/[^A-Z]/g,'').slice(0,2))}
                      title="Código de 2 letras usado en la referencia del proyecto (ej: RE)"
                      placeholder="—" maxLength={2}
                      style={{width:38,textAlign:'center',fontSize:12,fontWeight:700,color:'#7B0D0D',border:'1px solid #ddd',borderRadius:6,padding:'4px 2px'}}/>
                    <button onClick={()=>handleDelete(cat.id)}
                      style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',padding:4}}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{borderTop:'1px solid #eee',paddingTop:14}}>
            <div style={{fontSize:12,color:'#888',marginBottom:8}}>Agregar nueva categoría de {tab==='servicio'?'Servicio':'Bien'}:</div>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" value={newLabel} onChange={e=>setNewLabel(e.target.value)}
                placeholder={tab==='servicio'?'Ej: Impermeabilización':'Ej: Membrana'}
                onKeyDown={e=>e.key==='Enter'&&handleAdd()}
                style={{flex:1}}/>
              <input className="form-input" value={newCodigo} onChange={e=>setNewCodigo(e.target.value.toUpperCase().replace(/[^A-Z]/g,'').slice(0,2))}
                placeholder="Código" title="Código de 2 letras para la referencia del proyecto (opcional, se genera solo si lo dejas vacío)"
                maxLength={2} style={{width:80,textAlign:'center'}}/>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving||!newLabel.trim()}>
                {saving?<span className="spinner"/>:<Plus size={15}/>} Agregar
              </button>
            </div>
            <div style={{fontSize:11,color:'#aaa',marginTop:6}}>El código de 2 letras aparece en la referencia automática del proyecto (ej: PET-<strong>RE</strong>-0001). Si lo dejas vacío, se genera solo a partir del nombre — nunca se repite entre categorías, y si escribes uno que ya está en uso, el sistema te avisa.</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

const categoriaLabel = (cat) => cat === 'retiro' ? 'Retiro / Dinero entregado' : (cat || '—');

// ── Modal bitácora de gastos de obra (visible para asesor dueño / gerencia) ──
function ModalBitacoraGastos({ obraId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState(null);
  const [form, setForm] = useState({ fecha:new Date().toISOString().slice(0,10), detalle:'', valor:'' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/obras/${obraId}/gastos`);
      setItems(r.data.items||[]);
      setTotales(r.data.totales||null);
    } catch { toast.error('Error al cargar la bitácora'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [obraId]);

  const handleRetiro = async () => {
    if (!form.detalle || !form.valor) { toast.error('Completa detalle y valor'); return; }
    setSaving(true);
    try {
      await api.post(`/obras/${obraId}/gastos`, { categoria:'retiro', fecha:form.fecha, detalle:form.detalle, valor:parseFloat(form.valor) });
      setForm({ fecha:new Date().toISOString().slice(0,10), detalle:'', valor:'' });
      toast.success('Retiro registrado');
      await load();
    } catch(e) { toast.error(e.response?.data?.error||'Error'); }
    finally { setSaving(false); }
  };

  const gastosOperativos = items.filter(g=>g.categoria!=='retiro');
  const retiros = items.filter(g=>g.categoria==='retiro');

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h3 className="modal-title">Bitácora de gastos</h3>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{textAlign:'center',padding:30}}><span className="spinner"/></div> : (
            <>
              {totales && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:16}}>
                  {[
                    ['Retiros', totales.totalRetiros, '#2563eb'],
                    ['Facturado', totales.facturadoTotal, '#7B0D0D'],
                    ['Sin factura', totales.sinFacturaTotal, '#92400e'],
                    ['Saldo', totales.saldo, totales.saldo>=0?'#15803d':'#dc2626'],
                  ].map(([l,v,c])=>(
                    <div key={l} style={{padding:'8px 10px',background:'#fafafa',borderRadius:8,textAlign:'center'}}>
                      <div style={{fontSize:11,color:'#888'}}>{l}</div>
                      <div style={{fontSize:15,fontWeight:700,color:c}}>${fmt(v)}</div>
                    </div>))}
                </div>)}

              <div style={{padding:'12px 14px',background:'#f0f9ff',borderRadius:10,border:'1px solid #bae6fd',marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Registrar retiro / dinero entregado</div>
                <div style={{display:'flex',gap:8}}>
                  <input className="form-input" type="date" style={{width:140}} value={form.fecha} onChange={e=>setForm(p=>({...p,fecha:e.target.value}))}/>
                  <input className="form-input" style={{flex:1}} placeholder="Ej: Retiro de efectivo a Pedro Ruiz" value={form.detalle} onChange={e=>setForm(p=>({...p,detalle:e.target.value}))}/>
                  <input className="form-input" style={{width:110}} type="number" step="0.01" placeholder="Valor" value={form.valor} onChange={e=>setForm(p=>({...p,valor:e.target.value}))}/>
                  <button className="btn btn-primary btn-sm" onClick={handleRetiro} disabled={saving}>{saving?<span className="spinner"/>:'Registrar'}</button>
                </div>
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:'#888',marginBottom:6}}>RETIROS ({retiros.length})</div>
                {retiros.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin retiros registrados</div> : retiros.map(g=>(
                  <div key={g.id} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6,marginBottom:4}}>
                    <span>{g.fecha} — {g.detalle}</span>
                    <strong>${fmt(g.valor)}</strong>
                  </div>))}
              </div>

              <div>
                <div style={{fontSize:12,fontWeight:700,color:'#888',marginBottom:6}}>GASTOS OPERATIVOS ({gastosOperativos.length})</div>
                {gastosOperativos.length===0 ? <div style={{fontSize:12,color:'#aaa'}}>Sin gastos registrados aún</div> : gastosOperativos.map(g=>(
                  <div key={g.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,padding:'6px 10px',background:'#fafafa',borderRadius:6,marginBottom:4}}>
                    <span>
                      <span className={`badge ${g.conFactura?'badge-blue':'badge-gray'}`} style={{marginRight:6}}>{categoriaLabel(g.categoria)}</span>
                      {g.fecha} — {g.conFactura ? `Factura ${g.numeroFactura}` : (g.detalle||'—')}
                    </span>
                    <strong>${fmt(g.valor)}</strong>
                  </div>))}
              </div>
            </>)}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// Proyectos de tipo "bien": el producto se compra para revenderlo, no se consume en una obra con
// técnico. Aquí se registra cuánto costó de verdad adquirir cada producto de la cotización
// aprobada — eso es lo que se resta de la utilidad real (precio al cliente - costo de compra).
function ModalProductosBaja({ proyecto, puedeEditar, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [valores, setValores] = useState({}); // id -> texto del input mientras se edita
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${proyecto.id}/productos-baja`);
      setItems(r.data);
      const v = {};
      r.data.forEach(it => { v[it.id] = it.costoUnitario ?? ''; });
      setValores(v);
    } catch { toast.error('Error al cargar los productos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [proyecto.id]); // eslint-disable-line

  const handleGuardar = async (item) => {
    const val = valores[item.id];
    if (val === '' || val === undefined) { toast.error('Escribe el costo de adquisición'); return; }
    if (parseFloat(val) < 0) { toast.error('El costo no puede ser negativo'); return; }
    setSavingId(item.id);
    try {
      const r = await api.put(`/projects/${proyecto.id}/productos-baja/${item.id}`, { costoUnitario: parseFloat(val) });
      setItems(prev => prev.map(x => x.id === item.id ? r.data : x));
      toast.success(`"${item.nombre}" dado de baja`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSavingId(null); }
  };

  const esBien = proyecto.tipoVenta === 'bien';
  const pendientes = items.filter(it => it.costoUnitario === null || it.costoUnitario === undefined).length;
  const totalCosto = items.reduce((s, it) => s + Number(it.cantidad||0) * Number(it.costoUnitario||0), 0);
  const totalVenta = items.reduce((s, it) => s + Number(it.cantidad||0) * Number(it.vUnitarioVenta||0), 0);
  const gananciaEstimada = totalVenta - totalCosto;

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h3 className="modal-title">{esBien?'Dar de baja productos':'Costo real de materiales consumidos'}</h3>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">
          <div style={{marginBottom:14,padding:'10px 14px',background:'#f0f9ff',borderRadius:8,border:'1px solid #bae6fd',fontSize:13}}>
            Proyecto: <strong>{proyecto.referencia}</strong><br/>
            Cliente: <strong>{proyecto.client?.empresa}</strong>
          </div>

          {!puedeEditar && (
            <div style={{fontSize:12,color:'#92400e',background:'#fffbeb',border:'1px solid #fef3c7',borderRadius:8,padding:'8px 10px',marginBottom:14}}>
              Solo Gerencia o Administración pueden editar el costo de adquisición. Aquí puedes ver el estado, pero no modificarlo.
            </div>
          )}

          {loading ? <div style={{textAlign:'center',padding:30}}><span className="spinner"/></div> :
          items.length === 0 ? (
            <div style={{fontSize:13,color:'#888',textAlign:'center',padding:20}}>
              {esBien ? 'Este proyecto no tiene productos en su cotización aprobada aún.' : 'El técnico aún no ha registrado consumo de productos en la bitácora de obra.'}
            </div>
          ) : (
            <>
              <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
                {items.map(it => {
                  const dado = it.costoUnitario !== null && it.costoUnitario !== undefined;
                  const gananciaLinea = dado ? (Number(it.vUnitarioVenta||0) - Number(it.costoUnitario||0)) * Number(it.cantidad||0) : null;
                  return (
                    <div key={it.id} style={{padding:'10px 12px',background:dado?'#f0fdf4':'#fafafa',borderRadius:8,border:'1px solid '+(dado?'#bbf7d0':'#e5e5e5')}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8,flexWrap:'wrap',gap:6}}>
                        <div>
                          <span style={{fontSize:11,fontWeight:700,color:'#7B0D0D'}}>{it.codigo}</span>{' '}
                          <strong style={{fontSize:13}}>{it.nombre}</strong>
                          <div style={{fontSize:12,color:'#888'}}>{esBien?'Cantidad':'Cantidad consumida'}: {it.cantidad} {it.unidadMedida||''} · Precio al cliente: ${fmt(it.vUnitarioVenta)}/u</div>
                        </div>
                        {dado ? (
                          <span style={{fontSize:12,fontWeight:700,color:'#15803d'}}>{esBien?`Dado de baja — ganancia: $${fmt(gananciaLinea)}`:`Costo registrado: $${fmt(Number(it.cantidad||0)*Number(it.costoUnitario||0))}`}</span>
                        ) : (
                          <span style={{fontSize:12,fontWeight:600,color:'#d97706'}}>Pendiente</span>
                        )}
                      </div>
                      <div style={{display:'flex',gap:8,alignItems:'center'}}>
                        <div className="form-group" style={{margin:0,flex:1}}>
                          <label className="form-label" style={{fontSize:11}}>Costo de adquisición por unidad ($)</label>
                          <input className="form-input" type="number" step="0.01" min="0" disabled={!puedeEditar}
                            value={valores[it.id] ?? ''} onChange={e=>setValores(p=>({...p,[it.id]:e.target.value}))}
                            placeholder="Ej: 300.00"/>
                        </div>
                        {puedeEditar && (
                          <button className="btn btn-secondary btn-sm" onClick={()=>handleGuardar(it)} disabled={savingId===it.id} style={{marginTop:16}}>
                            {savingId===it.id?<span className="spinner"/>:(dado?'Actualizar':(esBien?'Dar de baja':'Registrar costo'))}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{padding:'12px 14px',background:pendientes>0?'#fffbeb':'#f0fdf4',borderRadius:10,border:'1px solid '+(pendientes>0?'#fef3c7':'#bbf7d0')}}>
                {pendientes>0 ? (
                  <div style={{fontSize:13,color:'#92400e',fontWeight:600}}>
                    Faltan {pendientes} de {items.length} producto(s) por {esBien?'dar de baja':'registrar su costo real'} — hasta completarlos, este proyecto no se puede finalizar.
                  </div>
                ) : esBien ? (
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,fontWeight:600,color:'#15803d'}}>
                    <span>Todos los productos dados de baja — ya puedes finalizar el proyecto.</span>
                    <span>Ganancia estimada: ${fmt(gananciaEstimada)}</span>
                  </div>
                ) : (
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,fontWeight:600,color:'#15803d'}}>
                    <span>Todos los materiales tienen costo real registrado — ya puedes finalizar el proyecto.</span>
                    <span>Costo total de materiales: ${fmt(totalCosto)}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// Factura de cierre: obligatoria para poder finalizar cualquier proyecto (servicio o bien).
function ModalFacturaFinalizar({ onClose, onConfirm, saving }) {
  const [numero, setNumero] = useState('');
  const [pdfNombre, setPdfNombre] = useState('');
  const [pdfData, setPdfData] = useState('');
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Solo se admiten archivos PDF'); return; }
    if (file.size > 10*1024*1024) { toast.error('El PDF no puede superar 10MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setPdfData(ev.target.result); setPdfNombre(file.name); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = () => {
    if (!numero.trim()) { toast.error('Escribe el número de factura'); return; }
    if (!pdfData) { toast.error('Sube el PDF de la factura'); return; }
    onConfirm({ facturaNumero: numero.trim(), facturaPdfNombre: pdfNombre, facturaPdfData: pdfData });
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:460}}>
        <div className="modal-header">
          <h3 className="modal-title">Finalizar proyecto</h3>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">
          <div style={{fontSize:13,color:'#555',marginBottom:14,padding:'10px 14px',background:'#fffbeb',border:'1px solid #fef3c7',borderRadius:8}}>
            Para finalizar este proyecto, adjunta el número y el PDF de la factura de cierre. Sin esto no se puede finalizar.
          </div>
          <div className="form-group">
            <label className="form-label">Número de factura</label>
            <input className="form-input" value={numero} onChange={e=>setNumero(e.target.value)} placeholder="Ej: 001-001-000123"/>
          </div>
          <div className="form-group">
            <label className="form-label">PDF de la factura</label>
            <button type="button" className="btn btn-secondary btn-sm" onClick={()=>fileRef.current.click()} style={{display:'block'}}>
              <Upload size={14}/> {pdfNombre || 'Seleccionar PDF'}
            </button>
            <input ref={fileRef} type="file" accept=".pdf" style={{display:'none'}} onChange={handleFile}/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving?<><span className="spinner"/>Guardando...</>:'Finalizar proyecto'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProyectosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isGerencia = ['admin','master','gerente_financiera','gerente_proyectos'].includes(user?.rol);
  const puedeEditarCostoBaja = ['admin','master','gerente_financiera','gerente_proyectos'].includes(user?.rol);
  const isMasterAdmin = ['admin','master'].includes(user?.rol);
  const puedeEditarProyecto = (p) => p.estado!=='finalizado' || isMasterAdmin;

  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteList, setShowClienteList] = useState(false);
  const clienteLabel = (c) => `${c.empresa}${c.contactoNombre ? ' — ' + c.contactoNombre : ''}`;
  const filePresRef = useRef();
  const fileOtrosRef = useRef();
  const verAbiertoRef = useRef(null); // id del proyecto que se está viendo (modal 'ver'), para refrescarlo en cada poll
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [modalTecnico, setModalTecnico] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoSel, setTecnicoSel] = useState('');
  const [savingTecnico, setSavingTecnico] = useState(false);
  const [modalProductosBaja, setModalProductosBaja] = useState(null); // proyecto seleccionado
  const [modalFactura, setModalFactura] = useState(false); // pide numero+PDF antes de finalizar
  const [showModalCats, setShowModalCats] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [diasEstimadosSel, setDiasEstimadosSel] = useState('');
  const [rosterSel, setRosterSel] = useState([]); // [{id?, alias, valorDia}]
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [modalGastos, setModalGastos] = useState(null); // obra seleccionada para ver bitácora

  const loadCategorias = async () => {
    try { const r = await api.get('/project-categories'); setCategorias(r.data); } catch {}
  };

  const parseDocumentos = (raw) => {
    try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || []); } catch(e) { return []; }
  };

  const load = async (silent=false) => {
    try {
      const calls = [api.get('/projects'), api.get('/clients')];
      if(isGerencia) calls.push(api.get('/admin/users'));
      const results = await Promise.all(calls);
      setProyectos(results[0].data);
      setClientes(results[1].data);
      if(isGerencia && results[2]) setTodosUsuarios(results[2].data);
      // Si el modal "Ver" está abierto, lo refrescamos también — así el costo en vivo,
      // la utilidad real y lo que el técnico va registrando se ven sin recargar la página.
      if (verAbiertoRef.current) {
        const fresh = results[0].data.find(p => p.id === verAbiertoRef.current);
        if (fresh) setSelected({ ...fresh, documentos: parseDocumentos(fresh.documentos) });
      }
    }
    catch { if(!silent) toast.error('Error al cargar'); } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/users/all').then(r => {
      setTecnicos(r.data.filter(u => u.rol === 'tecnico'));
    }).catch(() => {});
    loadCategorias();
  }, []);

  useEffect(() => { load(); }, []);

  // Actualización en tiempo real: mientras la página esté abierta, refresca los proyectos
  // (y el modal "Ver" si está abierto) cada 30s, sin necesidad de recargar.
  useEffect(() => {
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, []);

  // Llega precargado desde una cita completada (ver CitasPage → handleCrearProyecto)
  useEffect(() => {
    if (location.state?.prefillProyecto) {
      openCrear(location.state.prefillProyecto);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Llega desde un clic en la Agenda compartida sobre un evento de este proyecto
  useEffect(() => {
    if (location.state?.openProyectoId && proyectos.length > 0) {
      const p = proyectos.find(pr => pr.id === location.state.openProyectoId);
      if (p) openVer(p);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, proyectos]);

  const closeModalTecnico = () => {
    setModalTecnico(null); setTecnicoSel(''); setDiasEstimadosSel(''); setRosterSel([]);
  };

  const openModalTecnico = async (p) => {
    setModalTecnico(p);
    setTecnicoSel(p.obra?.tecnicoId || '');
    setDiasEstimadosSel(p.obra?.diasEstimados ?? '');
    if (p.obra?.id) {
      setLoadingRoster(true);
      try {
        const r = await api.get(`/obras/${p.obra.id}/personas`);
        setRosterSel(r.data.filter(x=>x.activo).map(x => ({ id: x.id, alias: x.alias, valorDia: x.valorDia })));
      } catch { setRosterSel([]); }
      finally { setLoadingRoster(false); }
    } else {
      setRosterSel([]);
    }
  };

  const addRosterRow = () => setRosterSel(prev => [...prev, { alias: `Ayudante ${prev.length + 1}`, valorDia: '' }]);
  const removeRosterRow = (idx) => setRosterSel(prev => prev.filter((_, i) => i !== idx));
  const updateRosterRow = (idx, k, v) => setRosterSel(prev => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  const gastoProyectadoVivo = rosterSel.reduce((s, p) => s + (parseFloat(p.valorDia) || 0), 0) * (parseFloat(diasEstimadosSel) || 0);

  const handleAsignarTecnico = async () => {
    if (!tecnicoSel) { toast.error('Selecciona un técnico'); return; }
    setSavingTecnico(true);
    try {
      const r = await api.post('/obras', {
        projectId: modalTecnico.id, tecnicoId: tecnicoSel,
        diasEstimados: diasEstimadosSel !== '' ? parseFloat(diasEstimadosSel) : null,
      });
      await api.put(`/obras/${r.data.id}/personas`, {
        personas: rosterSel.map(p => ({ id: p.id, alias: p.alias, valorDia: parseFloat(p.valorDia) || 0 })),
      });
      toast.success('Técnico asignado al proyecto');
      closeModalTecnico();
      await load();
    } catch(e) { toast.error(e.response?.data?.error || 'Error al asignar'); }
    finally { setSavingTecnico(false); }
  };

  const openCrear = (prefill = {}) => {
    setForm({ ...emptyForm, ...prefill }); setSelected(null); setModal('crear');
    const c = prefill.clientId ? clientes.find(x => x.id === prefill.clientId) : null;
    setSearchCliente(c ? clienteLabel(c) : ''); setShowClienteList(false);
  };
  const openEditar = (p) => {
    if(!puedeEditarProyecto(p)){ toast.error('Este proyecto ya fue finalizado. Solo Admin o Master pueden editarlo.'); return; }
    let docs = [];
    try { docs = typeof p.documentos === 'string' ? JSON.parse(p.documentos) : (p.documentos || []); } catch(e) { docs = []; }
    setForm({...emptyForm,...p, documentos: docs});
    setSelected(p); setModal('editar');
    const c = clientes.find(x => x.id === p.clientId);
    setSearchCliente(c ? clienteLabel(c) : ''); setShowClienteList(false);
  };
  const openVer = (p) => {
    let docs = [];
    try { docs = typeof p.documentos === 'string' ? JSON.parse(p.documentos) : (p.documentos || []); } catch(e) {}
    setSelected({...p, documentos: docs});
    setModal('ver');
    verAbiertoRef.current = p.id;
  };
  const closeModal = () => { setModal(null); setSelected(null); verAbiertoRef.current = null; };

  const f = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => {
      const next = { ...prev, [k]: val, gananciaBrutaProtegida: '20' };
      if (k === 'gastosAdministrativos') {
        const costo = parseFloat(prev.costo) || 0;
        const gastosAdmin = parseFloat(val) || 0;
        const presupuesto = parseFloat(prev.presupuesto) || 0;
        const costoTotal = costo + gastosAdmin;
        next.costoTotal = costoTotal.toFixed(2);
        next.utilidadReal = (presupuesto - costoTotal).toFixed(2);
        next.valorVariacion = (parseFloat(next.utilidadReal) - (parseFloat(prev.utilidad) || 0)).toFixed(2);
      }
      return next;
    });
  };

  const MAX_FILE_MB = 5;
  const handleUploadDoc = (tipo) => (e) => {
    Array.from(e.target.files).forEach(file => {
      if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`"${file.name}" supera ${MAX_FILE_MB}MB`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => setForm(prev=>({...prev,documentos:[...(prev.documentos||[]),{tipo,nombre:file.name,base64:ev.target.result}]}));
      reader.readAsDataURL(file);
    });
    e.target.value='';
  };
  const removeDoc = (idx) => setForm(prev=>({...prev,documentos:prev.documentos.filter((_,i)=>i!==idx)}));

  // Guarda de verdad — recibe el payload explícito (no lee `form` directo) para que el flujo de
  // "finalizar con factura" pueda guardar los datos de la factura en el mismo request en que los
  // acaba de recibir, sin depender de que setForm ya haya actualizado el estado para ese momento.
  const guardarProyecto = async (payload) => {
    setSaving(true);
    try {
      if(modal==='crear'){await api.post('/projects',payload);toast.success('Proyecto creado');}
      else{await api.put(`/projects/${selected.id}`,payload);toast.success('Proyecto actualizado');}
      await load(); closeModal(); setModalFactura(false);
    } catch(err){toast.error(err.response?.data?.error||'Error');}
    finally{setSaving(false);}
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if(!form.clientId){ toast.error('Busca y selecciona una empresa de la lista'); return; }
    if(form.aprobado && !['activo','pausa','finalizado'].includes(form.estado)){
      toast.error('El proyecto está aprobado — cambia el estado a Activo o En pausa');
      return;
    }
    // Finalizar requiere factura (número + PDF) — si no la tiene ya guardada, se pide antes de guardar.
    if(form.estado==='finalizado' && !(form.facturaNumero && form.facturaPdfData)){
      setModalFactura(true);
      return;
    }
    await guardarProyecto(form);
  };

  const handleConfirmarFactura = async ({ facturaNumero, facturaPdfNombre, facturaPdfData }) => {
    const payload = { ...form, facturaNumero, facturaPdfNombre, facturaPdfData };
    setForm(payload);
    await guardarProyecto(payload);
  };

  const handleDelete = async (p) => {
    if(!window.confirm(`¿Eliminar "${p.referencia}"?`)) return;
    try{await api.delete(`/projects/${p.id}`);toast.success('Eliminado');await load();}
    catch{toast.error('Error');}
  };

  const filtered = proyectos.filter(p=>{
    const ms=!search||p.referencia.toLowerCase().includes(search.toLowerCase())||(p.client?.empresa||'').toLowerCase().includes(search.toLowerCase());
    const me=!filtroEstado||p.estado===filtroEstado;
    const mu=!filtroUsuario||p.userId===filtroUsuario;
    return ms&&me&&mu;
  });

  // Categorías filtradas por tipo de venta del form
  const cats = categorias.filter(c => c.tipo === form.tipoVenta);

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Proyectos</h1><p className="page-subtitle">{proyectos.length} proyecto(s)</p></div>
        <div style={{display:'flex',gap:8}}>
          {isGerencia && (
            <button className="btn btn-secondary" onClick={()=>setShowModalCats(true)}>
              <Settings size={15}/> Gestionar categorías
            </button>
          )}
          <button className="btn btn-primary" onClick={() => openCrear()}><Plus size={16}/> Nuevo proyecto</button>
        </div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        {ESTADOS.map(e=>{const c=proyectos.filter(p=>p.estado===e.value).length;return(
          <button key={e.value} onClick={()=>setFiltroEstado(filtroEstado===e.value?'':e.value)}
            style={{padding:'6px 14px',borderRadius:20,border:`2px solid ${filtroEstado===e.value?'#7B0D0D':'#e5e5e5'}`,background:filtroEstado===e.value?'#fff5f5':'white',cursor:'pointer',fontSize:13,fontWeight:500}}>
            {e.label}: <strong>{c}</strong></button>);})}
      </div>

      <div className="filters-bar">
        <div className="search-box"><Search size={16}/>
          <input className="form-input" placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:38,width:300}}/>
        </div>
        {isGerencia && todosUsuarios.length > 0 && (
          <select className="form-select" style={{width:200}} value={filtroUsuario} onChange={e=>setFiltroUsuario(e.target.value)}>
            <option value="">Todos los asesores</option>
            {todosUsuarios.map(u=><option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
          </select>
        )}
        {(search||filtroEstado||filtroUsuario)&&(
          <button className="btn btn-secondary btn-sm" onClick={()=>{setSearch('');setFiltroEstado('');setFiltroUsuario('');}}>Limpiar</button>)}
      </div>

      <div className="card">
        {loading?<div style={{textAlign:'center',padding:40}}><div className="spinner spinner-dark" style={{width:30,height:30,margin:'0 auto'}}/></div>
        :filtered.length===0?<div className="empty-state"><FolderKanban size={48}/><h3>Sin proyectos</h3></div>
        :<div className="table-container"><table>
          <thead><tr><th>Referencia</th><th>Empresa</th><th>Tipo</th><th>Presupuesto final</th><th>Aprobado</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>{filtered.map(p=>(
            <tr key={p.id}>
              <td><strong>{p.referencia}</strong>
                {p.nombre&&<div style={{fontSize:12,color:'#888',marginTop:2}}>{p.nombre.slice(0,50)}</div>}</td>
              <td>{p.client?.empresa||'—'}<div style={{fontSize:12,color:'#888'}}>{p.client?.ciudad}</div></td>
              <td><span className={`badge ${p.tipoVenta==='servicio'?'badge-blue':'badge-orange'}`}>{p.tipoVenta==='servicio'?'Servicio':'Bien'}</span>
                <div style={{fontSize:11,color:'#888',marginTop:2}}>{p.categoriaVenta}</div></td>
              <td style={{fontWeight:600}}>${fmt(p.presupuestoFinal||p.presupuesto)}</td>
              <td>{p.aprobado?<span className="badge badge-green">Sí</span>:<span className="badge badge-gray">No</span>}</td>
              <td>{badgeEstado(p.estado)}</td>
              <td><div style={{display:'flex',gap:6}}>
                <button className="btn-icon" title="Ver" onClick={()=>openVer(p)}><Eye size={15}/></button>
                {p.tipoVenta==='servicio'&&!['finalizado','debaja'].includes(p.estado)&&(
                  <button className="btn-icon" title="Asignar técnico" onClick={()=>openModalTecnico(p)} style={{color:'#059669'}}>
                    <Wrench size={15}/>
                  </button>)}
                {!['finalizado','debaja'].includes(p.estado)&&(
                  <button className="btn-icon" title={p.tipoVenta==='bien'?'Dar de baja productos':'Costo real de materiales'} onClick={()=>setModalProductosBaja(p)} style={{color:'#059669'}}>
                    <PackageMinus size={15}/>
                  </button>)}
                <button className="btn-icon" title="Cotizaciones" onClick={()=>navigate('/cotizaciones')} style={{color:'#2563eb'}}><FileText size={15}/></button>
                {puedeEditarProyecto(p)&&(
                  <button className="btn-icon" title="Editar" onClick={()=>openEditar(p)}><Edit2 size={15}/></button>)}
                {p.estado!=='finalizado'&&(
                  <button className="btn-icon" title="Eliminar" onClick={()=>handleDelete(p)} style={{color:'#dc2626'}}><Trash2 size={15}/></button>)}
              </div></td>
            </tr>))}</tbody>
        </table></div>}
      </div>

      {(modal==='crear'||modal==='editar')&&(
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3 className="modal-title">{modal==='crear'?'Nuevo proyecto':'Editar proyecto'}</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18}/></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <Section title="Información general"/>
                <div className="form-group">
                  <label className="form-label">Empresa cliente *</label>
                  <div style={{position:'relative'}}>
                    <input className="form-input" autoComplete="off"
                      placeholder="Escribe para buscar o haz clic para ver todas..."
                      value={searchCliente}
                      onChange={e=>{ setSearchCliente(e.target.value); setShowClienteList(true); setForm(prev=>({...prev,clientId:''})); }}
                      onFocus={()=>setShowClienteList(true)} />
                    {showClienteList && (
                      <div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid #e5e5e5',borderRadius:8,maxHeight:240,overflowY:'auto',zIndex:100,boxShadow:'0 4px 16px rgba(0,0,0,0.12)',marginTop:2}}>
                        {clientes.filter(c=>!searchCliente||clienteLabel(c).toLowerCase().includes(searchCliente.toLowerCase())).slice(0,30).map(c=>(
                          <div key={c.id}
                            onClick={()=>{ setForm(prev=>({...prev,clientId:c.id})); setSearchCliente(clienteLabel(c)); setShowClienteList(false); }}
                            style={{padding:'8px 12px',cursor:'pointer',borderBottom:'1px solid #f0f0f0',fontSize:13}}
                            onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                            onMouseLeave={e=>e.currentTarget.style.background='white'}>
                            <strong>{c.empresa}</strong>{c.contactoNombre?<span style={{color:'#888'}}> — {c.contactoNombre}</span>:null}
                          </div>
                        ))}
                        {clientes.filter(c=>!searchCliente||clienteLabel(c).toLowerCase().includes(searchCliente.toLowerCase())).length===0&&(
                          <div style={{padding:14,textAlign:'center',color:'#888',fontSize:13}}>Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{padding:'10px 14px',background:'#f0f9ff',borderRadius:8,border:'1px solid #bae6fd',fontSize:13,color:'#0369a1',marginBottom:16}}>
                  La referencia se genera automáticamente: <strong>INICIALES-CODIGO-NÚMERO</strong>
                </div>
                {form.citaId && (
                  <div style={{padding:'10px 14px',background:'#f0fdf4',borderRadius:8,border:'1px solid #bbf7d0',fontSize:13,color:'#15803d',marginBottom:16}}>
                    Este proyecto se está creando a partir de una cita completada — empresa, contacto y descripción ya vienen precargados.
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Tipo de venta *</label>
                  <div style={{display:'flex',gap:12,marginTop:4}}>
                    {['servicio','bien'].map(t=>(
                      <label key={t} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'10px 20px',borderRadius:8,border:`2px solid ${form.tipoVenta===t?'#7B0D0D':'#e5e5e5'}`,background:form.tipoVenta===t?'#fff5f5':'white',fontSize:14,fontWeight:500}}>
                        <input type="radio" name="tipoVenta" value={t} checked={form.tipoVenta===t} onChange={()=>setForm(prev=>({...prev,tipoVenta:t,categoriaVenta:''}))} style={{accentColor:'#7B0D0D'}}/>
                        {t==='servicio'?' Servicio':' Bien'}
                      </label>))}
                  </div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span>{form.tipoVenta==='servicio'?'Tipo de servicio':'Objetivo del bien'} *</span>
                    </label>
                    {cats.length === 0 ? (
                      <div style={{padding:'10px 14px',background:'#fffbeb',borderRadius:8,border:'1px solid #fef3c7',fontSize:13,color:'#92400e'}}>
                        No hay categorías. {isGerencia ? 'Agrega desde "Gestionar categorías".' : 'Pide al administrador que agregue categorías.'}
                      </div>
                    ) : (
                      <select className="form-select" required value={form.categoriaVenta} onChange={f('categoriaVenta')}>
                        <option value="">Seleccionar...</option>
                        {cats.map(c=><option key={c.id} value={c.value}>{c.label}</option>)}
                      </select>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Persona de atención</label>
                    <input className="form-input" value={form.atencionNombre} onChange={f('atencionNombre')} placeholder="Nombre del contacto"/>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Nombre del proyecto</label>
                  <input className="form-input" value={form.nombre} onChange={f('nombre')} placeholder="Ej: Revestimiento planta principal"/></div>
                <div className="form-group"><label className="form-label">Descripción detallada</label>
                  <textarea className="form-textarea" rows={2} value={form.descripcion} onChange={f('descripcion')} placeholder="Detalles del alcance..."/></div>

                <Section title="Información financiera"/>
                <div className="form-group">
                  <label className="form-label">Presupuesto ($)</label>
                  <input className="form-input" type="number" step="0.01" value={form.presupuesto} readOnly
                    style={{fontSize:16,fontWeight:600,background:'#f0fdf4',color:'#15803d'}}/>
                  <span style={{fontSize:11,color:'#888',marginTop:2,display:'block'}}>Se completa automáticamente al aprobar una cotización de este proyecto.</span>
                </div>
                <div style={{padding:'12px 14px',background:'#fafafa',borderRadius:10,border:'1px solid #e5e5e5',marginBottom:14}}>
                  <div className="form-row form-row-2">
                    <div className="form-group" style={{margin:0}}>
                      <label className="form-label">Descuento aplicado</label>
                      <input className="form-input" value={form.descuentoCotizacion!=null?`${form.descuentoCotizacion}%`:'—'} readOnly/>
                      <span style={{fontSize:11,color:'#888',marginTop:2,display:'block'}}>Se toma de la cotización aprobada — no editable aquí.</span>
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label className="form-label">Presupuesto final ($) — automático</label>
                      <input className="form-input" type="number" value={form.presupuestoFinal||''} readOnly
                        style={{background:'#f0fdf4',color:'#15803d',fontWeight:700,fontSize:15}}/>
                    </div>
                  </div>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Costo ($)</label>
                    <input className="form-input" type="number" step="0.01" value={form.costo} readOnly placeholder="0.00"/>
                    <span style={{fontSize:11,color:'#888'}}>Costo en vivo (mano de obra + productos + gastos de obra) — automático.</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Gastos administrativos ($)</label>
                    <input className="form-input" type="number" step="0.01" value={form.gastosAdministrativos}
                      onChange={isGerencia?f('gastosAdministrativos'):undefined} readOnly={!isGerencia} placeholder="0.00"
                      style={!isGerencia?{background:'#f5f5f5',color:'#888',cursor:'not-allowed'}:undefined}/>
                    <span style={{fontSize:11,color:'#888'}}>{isGerencia?'Manual.':'Solo Gerencia o Administración puede editar este valor.'}</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Costo total ($)</label>
                    <input className="form-input" type="number" value={form.costoTotal||''} readOnly
                      style={{background:'#fff5f5',color:'#7B0D0D',fontWeight:700}}/>
                    <span style={{fontSize:11,color:'#888'}}>Costo + gastos administrativos — automático.</span>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
                  {[
                    {l:'Utilidad ($)',v:form.utilidad,c:'#2563eb',hint:'Meta fija: 20% del presupuesto'},
                    {l:'Ganancia bruta protegida',v:'20%',c:'#7B0D0D',hint:'Siempre 20% fijo'},
                    {l:'Utilidad real ($)',v:form.utilidadReal,c:'#15803d',hint:'Presupuesto − costo total (automático)'},
                  ].map(item=>(
                    <div key={item.l} style={{padding:'10px 12px',background:'#fafafa',borderRadius:8,border:'1px solid #f0f0f0'}}>
                      <div style={{fontSize:11,color:'#888',marginBottom:4}}>{item.l}</div>
                      <div style={{fontSize:18,fontWeight:700,color:item.c}}>{item.v||'0.00'}</div>
                      <div style={{fontSize:10,color:'#aaa',marginTop:3}}>{item.hint}</div>
                    </div>))}
                </div>
                <div style={{padding:'10px 14px',background:parseFloat(form.valorVariacion||0)>=0?'#f0fdf4':'#fff5f5',borderRadius:8,border:`1px solid ${parseFloat(form.valorVariacion||0)>=0?'#bbf7d0':'#fecaca'}`,marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:12,color:'#555',fontWeight:600}}>Valor de variación <span style={{fontSize:11,fontWeight:400,color:'#888'}}>(Utilidad real − Utilidad presupuestada)</span></div>
                  </div>
                  <div style={{fontSize:22,fontWeight:800,color:parseFloat(form.valorVariacion||0)>=0?'#15803d':'#dc2626'}}>
                    {parseFloat(form.valorVariacion||0)>=0?'+':''}${parseFloat(form.valorVariacion||0).toFixed(2)}
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Forma de pago</label>
                  <select className="form-select" value={form.formaPago} onChange={f('formaPago')}>
                    <option value="">Seleccionar...</option>
                    {FORMAS_PAGO.map(fp=><option key={fp} value={fp}>{fp}</option>)}
                  </select></div>

                <Section title="Estado y fechas"/>
                <div className="form-row form-row-2">
                  <div className="form-group"><label className="form-label">Estado</label>
                    <select className="form-select" value={form.estado} onChange={ev=>{
                      const val=ev.target.value;
                      if(val==='finalizado'&&!isGerencia){toast.error('Solo Gerencia o Admin puede finalizar un proyecto');return;}
                      setForm(prev=>({...prev,estado:val}));
                    }}>
                      {ESTADOS.filter(s=>isGerencia||s.value!=='finalizado').map(e=><option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>
                    {form.aprobado&&!['activo','pausa','finalizado'].includes(form.estado)&&(
                      <div style={{fontSize:12,color:'#dc2626',marginTop:4,padding:'4px 8px',background:'#fff5f5',borderRadius:6}}>
                        Proyecto aprobado — cambia el estado a Activo o En pausa
                      </div>)}</div>
                  <div style={{display:'flex',gap:20,alignItems:'center',paddingTop:24}}>
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14}}>
                      <input type="checkbox" checked={form.aprobado} onChange={f('aprobado')} style={{width:16,height:16,accentColor:'#7B0D0D'}}/>Proyecto aprobado</label>
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14}}>
                      <input type="checkbox" checked={form.relevancia} onChange={f('relevancia')} style={{width:16,height:16,accentColor:'#7B0D0D'}}/>Relevante</label>
                  </div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group"><label className="form-label">Fecha envío oferta</label><input className="form-input" type="date" value={form.fechaEnvioOferta} onChange={f('fechaEnvioOferta')}/></div>
                  <div className="form-group"><label className="form-label">Fecha inicio obra</label><input className="form-input" type="date" value={form.fechaInicioObra} onChange={f('fechaInicioObra')}/></div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group"><label className="form-label">Fecha fin estimada</label><input className="form-input" type="date" value={form.fechaFinEstimada} onChange={f('fechaFinEstimada')}/></div>
                  <div className="form-group"><label className="form-label">Fecha fin real</label><input className="form-input" type="date" value={form.fechaFinReal} onChange={f('fechaFinReal')}/></div>
                </div>

                <Section title="Seguimiento y documentos"/>
                <div className="form-group"><label className="form-label">Seguimiento / observaciones</label>
                  <textarea className="form-textarea" rows={3} value={form.seguimiento} onChange={f('seguimiento')} placeholder="Estado actual, pendientes, próximos pasos..."/></div>
                <div style={{marginBottom:12}}>
                  <label className="form-label">Presupuesto</label>
                  <div style={{display:'flex',gap:8,marginTop:6}}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={()=>{filePresRef.current.accept='.xlsx,.xls';filePresRef.current.click();}}><Upload size={14}/> Excel</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={()=>{filePresRef.current.accept='.pdf';filePresRef.current.click();}}><Upload size={14}/> PDF</button>
                  </div>
                  <input ref={filePresRef} type="file" style={{display:'none'}} onChange={handleUploadDoc('presupuesto')}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label className="form-label">Otros documentos</label>
                  <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:6,display:'block'}} onClick={()=>fileOtrosRef.current.click()}><Upload size={14}/> Agregar</button>
                  <input ref={fileOtrosRef} type="file" multiple style={{display:'none'}} onChange={handleUploadDoc('otro')}/>
                </div>
                {(form.documentos||[]).length>0&&(
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {form.documentos.map((doc,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#fafafa',borderRadius:8,border:'1px solid #e5e5e5'}}>
                        <span style={{fontSize:13,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.nombre}</span>
                        <span className={`badge ${doc.tipo==='presupuesto'?'badge-blue':'badge-gray'}`}>{doc.tipo}</span>
                        <button type="button" onClick={()=>removeDoc(i)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',padding:4}}><X size={14}/></button>
                      </div>))}
                  </div>)}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?<><span className="spinner"/>Guardando...</>:'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>)}

      {modal==='ver'&&selected&&(
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <div><h3 className="modal-title">{selected.referencia}</h3>
                <div style={{marginTop:4}}>{badgeEstado(selected.estado)}{selected.aprobado&&<span className="badge badge-green" style={{marginLeft:6}}>Aprobado</span>}</div></div>
              <button className="btn-icon" onClick={closeModal}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
                {[['Empresa',selected.client?.empresa],['Tipo',`${selected.tipoVenta==='servicio'?'Servicio':'Bien'} — ${selected.categoriaVenta}`],
                  ['Presupuesto',`$${fmt(selected.presupuesto)}`],['Presupuesto final',`$${fmt(selected.presupuestoFinal)}`],
                  ['Costo',`$${fmt(selected.costo)}`],['Utilidad',`$${fmt(selected.utilidad)}`],
                  ['Utilidad real',`$${fmt(selected.utilidadReal)}`],['Variación',`$${fmt(selected.valorVariacion)}`],
                  ['Forma de pago',selected.formaPago],['Inicio obra',selected.fechaInicioObra],
                ].filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{padding:'8px 12px',background:'#fafafa',borderRadius:8}}>
                    <div style={{fontSize:12,color:'#888'}}>{k}</div>
                    <div style={{fontSize:14,fontWeight:500}}>{v}</div>
                  </div>))}
              </div>
              {selected.seguimiento&&<div style={{padding:14,background:'#fffbeb',borderRadius:8,border:'1px solid #fef3c7',marginBottom:12}}>
                <h4 style={{fontSize:13,fontWeight:600,color:'#92400e',marginBottom:6}}>SEGUIMIENTO</h4>
                <p style={{fontSize:14,lineHeight:1.6}}>{selected.seguimiento}</p></div>}
              {(selected.documentos||[]).length>0&&<div>
                <h4 style={{fontSize:13,fontWeight:600,marginBottom:8}}>DOCUMENTOS</h4>
                {selected.documentos.map((d,i)=>(
                  <a key={i} href={d.base64} download={d.nombre} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'#fafafa',borderRadius:8,marginBottom:6,textDecoration:'none',color:'#333',fontSize:13}}>
                    <span style={{flex:1}}>{d.nombre}</span>
                    <span style={{fontSize:11,color:'#7B0D0D'}}>Descargar</span>
                  </a>))}</div>}
              {selected.facturaNumero&&<div style={{padding:14,background:'#f0fdf4',borderRadius:8,border:'1px solid #bbf7d0',marginBottom:12}}>
                <h4 style={{fontSize:13,fontWeight:600,color:'#15803d',marginBottom:6}}>FACTURA DE CIERRE</h4>
                <div style={{display:'flex',alignItems:'center',gap:16,fontSize:14,flexWrap:'wrap'}}>
                  <span>N° {selected.facturaNumero}</span>
                  {selected.facturaPdfData&&<>
                    <button type="button" onClick={()=>verPdfEnNuevaPestana(selected.facturaPdfData)} style={{display:'flex',alignItems:'center',gap:6,color:'#7B0D0D',fontSize:13,fontWeight:500,background:'none',border:'none',cursor:'pointer',padding:0}}><Eye size={14}/> Ver PDF</button>
                    <a href={selected.facturaPdfData} download={selected.facturaPdfNombre||'factura.pdf'} style={{display:'flex',alignItems:'center',gap:6,color:'#7B0D0D',fontSize:13,fontWeight:500,textDecoration:'none'}}><FileText size={14}/> Descargar PDF</a>
                  </>}
                </div>
              </div>}
              {(isGerencia || selected.userId === user?.id) && selected.obra && (
                <div style={{marginTop:20,borderTop:'2px solid #fee2e2',paddingTop:16,padding:'16px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#7B0D0D'}}>Obra — Anotaciones del Técnico</div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setModalGastos(selected.obra.id)}>
                      Ver bitácora de gastos
                    </button>
                  </div>
                  {selected.obra.diasEstimados != null && (
                    <div style={{display:'flex',gap:10,marginBottom:10}}>
                      <div style={{flex:1,padding:'8px 12px',background:'#fafafa',borderRadius:8}}>
                        <div style={{fontSize:11,color:'#888'}}>Días estimados</div>
                        <div style={{fontSize:14,fontWeight:600}}>{selected.obra.diasEstimados}</div>
                      </div>
                      <div style={{flex:1,padding:'8px 12px',background:'#f0fdf4',borderRadius:8}}>
                        <div style={{fontSize:11,color:'#888'}}>Gasto proyectado en mano de obra</div>
                        <div style={{fontSize:14,fontWeight:700,color:'#15803d'}}>${fmt(selected.obra.gastoProyectadoManoObra)}</div>
                      </div>
                    </div>)}
                  {selected.obra.instrumentos && (
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#888',marginBottom:4}}>Instrumentos / Materiales</div>
                      <div style={{fontSize:13,whiteSpace:'pre-wrap',padding:'8px 12px',background:'#fafafa',borderRadius:6}}>{selected.obra.instrumentos}</div>
                    </div>)}
                  {(selected.obra.personasRoster||[]).filter(p=>p.activo).length > 0 && (
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#888',marginBottom:4}}>
                        Personal ({selected.obra.personasRoster.filter(p=>p.activo).length} personas)
                      </div>
                      {(() => {
                        let diasObra = [];
                        try { diasObra = JSON.parse(selected.obra.diasTrabajo || '[]'); } catch {}
                        return selected.obra.personasRoster.filter(p=>p.activo).map((p) => {
                          const { extra } = totalesPorPersona(diasObra, p.id);
                          return (
                            <div key={p.id} style={{fontSize:13,padding:'4px 12px',background:'#fafafa',borderRadius:6,marginBottom:4,display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                              <span>{p.alias}{p.nombreReal?` — ${p.nombreReal}`:''}{p.cedula?` (Cédula: ${p.cedula})`:''}</span>
                              <span style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                                {extra>0 && <span style={{color:'#b45309',fontWeight:600,fontSize:12}} title="Horas extra acumuladas (jornada estándar: 8h/día)">+{fmtHoras(extra)} extra</span>}
                                <span style={{color:'#15803d',fontWeight:600}}>${fmt(p.valorDia)}/día</span>
                              </span>
                            </div>);
                        });
                      })()}
                    </div>)}
                  {selected.obra.notas && (
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:'#888',marginBottom:4}}>Notas</div>
                      <div style={{fontSize:13,whiteSpace:'pre-wrap',padding:'8px 12px',background:'#fafafa',borderRadius:6}}>{selected.obra.notas}</div>
                    </div>)}
                </div>)}
              {(isGerencia || selected.userId === user?.id) && selected.costoEnVivo && (
                <div style={{marginTop:20,borderTop:'2px solid #fee2e2',paddingTop:16,padding:'16px'}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#7B0D0D',marginBottom:12}}>Costo del proyecto (en vivo)</div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {[
                      ['Mano de obra', selected.costoEnVivo.manoObra],
                      ['Gastos de obra (incl. retiros)', selected.costoEnVivo.gastosObraTotal],
                      [selected.tipoVenta==='bien'?'Productos dados de baja (costo de compra)':'Materiales consumidos (costo real)', selected.costoProductosBaja],
                    ].map(([label,val])=>(
                      <div key={label} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 12px',background:'#fafafa',borderRadius:6}}>
                        <span style={{color:'#555'}}>{label}</span><span style={{fontWeight:600}}>${fmt(val)}</span>
                      </div>))}
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:15,padding:'8px 12px',background:'#f0fdf4',borderRadius:6,border:'1px solid #bbf7d0',marginTop:4}}>
                      <span style={{fontWeight:700,color:'#15803d'}}>Total costo operativo</span>
                      <span style={{fontWeight:800,color:'#15803d'}}>${fmt(selected.costo)}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 12px',background:'#fafafa',borderRadius:6}}>
                      <span style={{color:'#555'}}>Gastos administrativos</span><span style={{fontWeight:600}}>${fmt(selected.gastosAdministrativos)}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:15,padding:'8px 12px',background:'#fff5f5',borderRadius:6,border:'1px solid #fecaca',marginTop:2}}>
                      <span style={{fontWeight:700,color:'#7B0D0D'}}>Costo total</span>
                      <span style={{fontWeight:800,color:'#7B0D0D'}}>${fmt(selected.costoTotal)}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:15,padding:'8px 12px',background:parseFloat(selected.utilidadReal||0)>=0?'#eff6ff':'#fff5f5',borderRadius:6,border:`1px solid ${parseFloat(selected.utilidadReal||0)>=0?'#bfdbfe':'#fecaca'}`,marginTop:2}}>
                      <span style={{fontWeight:700,color:parseFloat(selected.utilidadReal||0)>=0?'#1d4ed8':'#dc2626'}}>Utilidad real (presupuesto − costo total)</span>
                      <span style={{fontWeight:800,color:parseFloat(selected.utilidadReal||0)>=0?'#1d4ed8':'#dc2626'}}>${fmt(selected.utilidadReal)}</span>
                    </div>
                  </div>
                </div>)}
              {(isGerencia || selected.userId === user?.id) && (selected.quotations||[]).filter(q=>q.notas&&q.notas.trim()).length>0&&(
                <div style={{marginTop:20,borderTop:'2px solid #fee2e2',paddingTop:16,padding:'16px'}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#7B0D0D',marginBottom:12}}>Notas internas de cotizaciones</div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {selected.quotations.filter(q=>q.notas&&q.notas.trim()).map(q=>(
                      <div key={q.id} style={{padding:'8px 12px',background:'#fafafa',borderRadius:8}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                          <span style={{fontSize:11,fontWeight:700,color:'#7B0D0D'}}>{q.numero}</span>
                          {q.estado==='aprobada'&&<span className="badge badge-green">Cotización aprobada</span>}
                        </div>
                        <div style={{fontSize:13,whiteSpace:'pre-wrap'}}>{q.notas}</div>
                      </div>))}
                  </div>
                </div>)}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cerrar</button>
              {puedeEditarProyecto(selected)&&(
                <button className="btn btn-primary" onClick={()=>{closeModal();setTimeout(()=>openEditar(selected),100);}}>Editar</button>)}
            </div>
          </div>
        </div>)}

      {modalTecnico && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-header">
              <h3 className="modal-title">Asignar Técnico</h3>
              <button className="btn-icon" onClick={closeModalTecnico}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom:14,padding:'10px 14px',background:'#f0f9ff',borderRadius:8,border:'1px solid #bae6fd',fontSize:13}}>
                Proyecto: <strong>{modalTecnico.referencia}</strong><br/>
                Cliente: <strong>{modalTecnico.client?.empresa}</strong>
              </div>
              {tecnicos.length === 0 ? (
                <div style={{padding:'16px',textAlign:'center',color:'#888',fontSize:13}}>
                  No hay técnicos registrados.<br/>
                  <span style={{fontSize:12}}>Ve a Panel Admin → Usuarios → cambiar rol a "Técnico".</span>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Seleccionar técnico</label>
                    <select className="form-select" value={tecnicoSel} onChange={e=>setTecnicoSel(e.target.value)}>
                      <option value="">Seleccionar técnico...</option>
                      {tecnicos.map(t=><option key={t.id} value={t.id}>{t.nombre} {t.apellido}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Días estimados de trabajo</label>
                    <input className="form-input" type="number" min="0" value={diasEstimadosSel}
                      onChange={e=>setDiasEstimadosSel(e.target.value)} placeholder="Ej: 10"/>
                  </div>

                  <div style={{padding:'12px 14px',background:'#fafafa',borderRadius:10,border:'1px solid #e5e5e5',marginBottom:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                      <label className="form-label" style={{margin:0}}>Personal (PAX)</label>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addRosterRow}>
                        <Plus size={13}/> Agregar persona
                      </button>
                    </div>
                    {loadingRoster ? (
                      <div style={{textAlign:'center',padding:10}}><span className="spinner"/></div>
                    ) : rosterSel.length === 0 ? (
                      <div style={{fontSize:12,color:'#aaa',textAlign:'center',padding:8}}>Sin personal agregado aún</div>
                    ) : (
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {rosterSel.map((p,idx)=>(
                          <div key={p.id||idx} style={{display:'flex',gap:8,alignItems:'center'}}>
                            <input className="form-input" style={{flex:1}} value={p.alias}
                              onChange={e=>updateRosterRow(idx,'alias',e.target.value)} placeholder="Nombre / alias"/>
                            <input className="form-input" style={{width:110}} type="number" step="0.01" value={p.valorDia}
                              onChange={e=>updateRosterRow(idx,'valorDia',e.target.value)} placeholder="Valor/día"/>
                            <button type="button" onClick={()=>removeRosterRow(idx)}
                              style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',padding:4}}>
                              <X size={14}/>
                            </button>
                          </div>))}
                      </div>)}
                  </div>

                  <div style={{padding:'10px 14px',background:'#f0fdf4',borderRadius:8,border:'1px solid #bbf7d0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:12,color:'#555',fontWeight:600}}>Gasto proyectado en mano de obra</span>
                    <span style={{fontSize:18,fontWeight:800,color:'#15803d'}}>${fmt(gastoProyectadoVivo)}</span>
                  </div>
                </>)}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModalTecnico}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleAsignarTecnico} disabled={savingTecnico||!tecnicoSel}>
                {savingTecnico?<><span className="spinner"/>Asignando...</>:<><Wrench size={15}/> Asignar</>}
              </button>
            </div>
          </div>
        </div>)}

      {modalFactura && (
        <ModalFacturaFinalizar
          onClose={()=>setModalFactura(false)}
          onConfirm={handleConfirmarFactura}
          saving={saving}
        />
      )}

      {modalProductosBaja && (
        <ModalProductosBaja
          proyecto={modalProductosBaja}
          puedeEditar={puedeEditarCostoBaja}
          onClose={()=>setModalProductosBaja(null)}
          onSaved={()=>load()}
        />
      )}

      {modalGastos && (
        <ModalBitacoraGastos obraId={modalGastos} onClose={()=>setModalGastos(null)}/>
      )}

      {showModalCats && (
        <ModalCategorias
          onClose={() => setShowModalCats(false)}
          onSaved={() => loadCategorias()}
        />
      )}
    </div>
  );
}
